'use client'

import { LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import CloudDownloadOutlined from '@mui/icons-material/CloudDownloadOutlined'
import ForestOutlined from '@mui/icons-material/ForestOutlined'
import GradientOutlined from '@mui/icons-material/GradientOutlined'
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined'
import PercentOutlined from '@mui/icons-material/PercentOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import SafetyDivider from '@mui/icons-material/SafetyDivider'
import SavingsOutlined from '@mui/icons-material/SavingsOutlined'
import Storefront from '@mui/icons-material/Storefront'
import Box from '@mui/material/Box'
import { FC, ReactNode, useState } from 'react'
import Carousel from 'react-multi-carousel'

import FullScreenOnMobileWrapper from '@/components/FullScreenOnMobileWrapper'
import CheckoutImagesMobile from '@/components/InteractiveCheckout/CheckoutImagesMobile'
import MobileMapLegendBox from '@/components/InteractiveMap/MobileMapLegendBox'

import 'react-multi-carousel/lib/styles.css'

type MobileCarouselCheckoutProps = {
    items: {
        label: ReactNode
        icon: ReactNode
    }[]
}

const mobileCheckoutCarouselCards: MobileCarouselCheckoutProps[] = [
    {
        items: [
            {
                label: 'Merchant pays',
                icon: <Storefront />,
            },
            {
                label: 'Percentage contribution',
                icon: <PercentOutlined />,
            },
            {
                label: 'Reforesting the UK',
                icon: <ForestOutlined />,
            },
        ],
    },
    {
        items: [
            {
                label: 'Consumer pays',
                icon: <PersonOutlined />,
            },
            {
                label: 'Round-up',
                icon: <SavingsOutlined />,
            },
            {
                label: 'Direct Air Capture',
                icon: <CloudDownloadOutlined />,
            },
        ],
    },
    {
        items: [
            {
                label: 'Split',
                icon: <SafetyDivider />,
            },
            {
                label: 'Offset delivery',
                icon: <LocalShippingOutlined />,
            },
            {
                label: 'Biochar',
                icon: <GradientOutlined />,
            },
        ],
    },
]

const MobileCarouselCheckout: FC = () => {
    const { xsScreen } = useDevice()
    const [activeSlider, setActiveSlider] = useState<number>(0)
    const { palette } = LuneTheme
    return (
        <FullScreenOnMobileWrapper
            sx={{
                height: '720px',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                backgroundColor: `Grey300`,
                position: 'relative',
                '.react-multi-carousel-track  ': {
                    marginLeft: xsScreen ? '40px' : '150px',
                    justifyContent: 'stretch',
                    alignItems: 'stretch',
                },
            }}
        >
            <CheckoutImagesMobile activeImageIndex={activeSlider > 2 ? 0 : activeSlider} />
            <Box sx={{ width: '100%', mb: 6 }}>
                <Carousel
                    infinite={true}
                    partialVisible={true}
                    showDots={false}
                    arrows={false}
                    slidesToSlide={1}
                    swipeable
                    rewind={false}
                    afterChange={(newActive) => setActiveSlider(newActive - 1)}
                    ssr={true}
                    responsive={{
                        all: {
                            breakpoint: {
                                max: 10000,
                                min: 0,
                            },
                            items: 1,
                            partialVisibilityGutter: xsScreen ? 80 : 300,
                        },
                    }}
                >
                    {mobileCheckoutCarouselCards.map((card, index) => (
                        <Box
                            key={index}
                            sx={{
                                pl: 1,
                                pr: 1,
                                height: '100%',
                            }}
                        >
                            <MobileMapLegendBox
                                items={card.items}
                                sx={{
                                    color: palette.Green500,
                                    borderColor: palette.Green500,
                                }}
                                calculateItemsFontWeight={(isFirstItem) =>
                                    isFirstItem ? 'bold' : 'initial'
                                }
                            />
                        </Box>
                    ))}
                </Carousel>
            </Box>
        </FullScreenOnMobileWrapper>
    )
}

export default MobileCarouselCheckout
